import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  EREIGNIS_LABEL,
  ORTSART_LABEL,
  datumAusSpalten,
  datumKurz,
  jahrKurz,
  formatDatum,
  type Genauigkeit,
} from "@stammbaum/shared";
import { api } from "../api";
import { useTitel } from "../titel";

/**
 * Die Karte.
 *
 * Leaflet direkt, ohne React-Wrapper: die Karte ist ein Stück fremder
 * DOM, das React gar nicht anfassen soll, und die Wrapper koppeln sich an
 * React-Hauptversionen, die hier alle paar Jahre wechseln. Ein `useEffect`,
 * der die Karte einmal anlegt, und ein zweiter, der die Punkte neu setzt,
 * sind zusammen kürzer als die Abhängigkeit.
 *
 * Gezeichnet wird mit `circleMarker`, nicht mit Bildmarkern. Das spart die
 * Bilddateien (deren Pfade unter Bündlern reihum kaputtgehen), zeichnet
 * tausend Punkte ohne Ruckeln, und der Radius kann sagen, wie viel an einem
 * Ort hängt — was auf einer Familienkarte die eigentliche Auskunft ist.
 *
 * Kartenkacheln kommen von OpenStreetMap. Die Namensnennung unten rechts
 * ist Bedingung und darf nicht weg.
 */
/**
 * Datumstext eines Ereignisses.
 *
 * `genauigkeit` ist in der Datenbank nullable, `formatDatum` verlangt sie -
 * fehlt sie, ist "jahr" die ehrliche Annahme: lieber „1887" als ein
 * erfundener 1. Jaenner.
 */
function wann(e: {
  datumVon: string | null;
  datumBis: string | null;
  datumGenauigkeit: Genauigkeit | null;
  datumText: string | null;
}): string {
  if (!e.datumVon && !e.datumBis && !e.datumText) return "";
  return formatDatum({
    von: e.datumVon,
    bis: e.datumBis,
    genauigkeit: e.datumGenauigkeit ?? "jahr",
    text: e.datumText,
  });
}

export default function Karte() {
  useTitel("Karte");
  const [gewaehlt, setGewaehlt] = useState<number | null>(null);
  const [suche, setSuche] = useState("");
  const behaelter = useRef<HTMLDivElement | null>(null);
  const karte = useRef<L.Map | null>(null);
  const ebene = useRef<L.LayerGroup | null>(null);
  /** Die Kreise nach Ortsnummer - damit die Suche sie umfaerben kann,
   *  statt bei jedem Tastendruck tausend neue zu zeichnen. */
  const marken = useRef(new Map<number, L.CircleMarker>());
  /** Nur beim ersten Datensatz auf die Punkte zoomen, nicht bei jedem. */
  const eingepasst = useRef(false);

  const { data, isPending } = useQuery({ queryKey: ["karte"], queryFn: api.karte });

  const detail = useQuery({
    queryKey: ["karteOrt", gewaehlt],
    queryFn: () => api.karteOrt(gewaehlt!),
    enabled: gewaehlt !== null,
  });

  // --- Karte anlegen, einmal -------------------------------------------------
  useEffect(() => {
    if (!behaelter.current || karte.current) return;

    const m = L.map(behaelter.current, { scrollWheelZoom: true }).setView([48.2, 15.6], 7);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(m);
    ebene.current = L.layerGroup().addTo(m);
    karte.current = m;
    // Die Karte ist neu, also ist auch der Ausschnitt neu. Ohne dieses
    // Zuruecksetzen bleibt sie beim doppelten Mounten (StrictMode, oder
    // Zurueckkehren auf die Seite mit gefülltem Zwischenspeicher) auf dem
    // Startausschnitt stehen, weil `fitBounds` schon gelaufen zu sein
    // scheint.
    eingepasst.current = false;

    return () => {
      m.remove();
      karte.current = null;
      ebene.current = null;
      marken.current.clear();
      eingepasst.current = false;
    };
  }, []);

  /**
   * Treffer der Ortssuche. Gesucht wird im ganzen Pfad, damit „Echsenbach"
   * auch die Häuser darin findet — dieselbe Logik wie in der
   * Vorschlagsliste der Masken, nur hier im Browser, weil die Punkte
   * ohnehin schon alle da sind.
   */
  const text = suche.trim().toLowerCase();
  const treffer = useMemo(() => {
    if (!data || !text) return null;
    return data.punkte.filter((p) => p.pfad.toLowerCase().includes(text));
  }, [data, text]);
  const trefferIds = useMemo(() => (treffer ? new Set(treffer.map((p) => p.id)) : null), [treffer]);

  /** Zu einem Ort springen und ihn rechts aufschlagen. */
  function springe(p: { id: number; breitengrad: number; laengengrad: number }) {
    setGewaehlt(p.id);
    karte.current?.flyTo([p.breitengrad, p.laengengrad], Math.max(karte.current.getZoom(), 12), {
      duration: 0.6,
    });
  }

  /** Wie groß ein Punkt ist: Wurzel statt linear, sonst verdeckt der
   *  Heimatort mit 400 Einträgen die halbe Karte. */
  const groesse = (menge: number) => Math.min(22, 5 + Math.sqrt(menge) * 2.2);

  // --- Punkte setzen, einmal je Datensatz ------------------------------------
  useEffect(() => {
    const schicht = ebene.current;
    if (!schicht || !data) return;
    schicht.clearLayers();
    marken.current.clear();

    for (const p of data.punkte) {
      const menge = p.personen + p.ereignisse;
      const kreis = L.circleMarker([p.breitengrad, p.laengengrad], {
        radius: groesse(menge),
        weight: 1.5,
        color: "#7c2d12",
        fillColor: "#ea580c",
        fillOpacity: menge > 0 ? 0.55 : 0.15,
      })
        .bindTooltip(
          `${p.name}${menge > 0 ? ` — ${p.personen} Personen, ${p.ereignisse} Ereignisse` : ""}`,
        )
        .on("click", () => setGewaehlt(p.id))
        .addTo(schicht);
      marken.current.set(p.id, kreis);
    }

    if (!eingepasst.current && data.punkte.length > 0) {
      const grenzen = L.latLngBounds(data.punkte.map((p) => [p.breitengrad, p.laengengrad]));
      karte.current?.fitBounds(grenzen, { padding: [30, 30], maxZoom: 12 });
      eingepasst.current = true;
    }
  }, [data]);

  /**
   * Die Suche faerbt nur um, sie zeichnet nicht neu. Tausend Kreise bei
   * jedem Tastendruck wegzuwerfen und neu anzulegen macht das Tippen zäh.
   *
   * Nicht passende Punkte bleiben stehen, nur blass: ganz auszublenden
   * nimmt die Umgebung weg, und gerade die sagt bei einem Ortsnamen, ob man
   * den richtigen erwischt hat.
   */
  useEffect(() => {
    if (!data) return;
    for (const p of data.punkte) {
      const kreis = marken.current.get(p.id);
      if (!kreis) continue;
      const menge = p.personen + p.ereignisse;
      const daneben = trefferIds !== null && !trefferIds.has(p.id);
      kreis.setStyle({
        weight: daneben ? 1 : 1.5,
        color: daneben ? "#a3a3a3" : "#7c2d12",
        fillColor: daneben ? "#d4d4d4" : "#ea580c",
        fillOpacity: daneben ? 0.35 : menge > 0 ? 0.55 : 0.15,
      });
      kreis.setRadius(daneben ? Math.min(groesse(menge), 6) : groesse(menge));
    }
  }, [data, trefferIds]);

  /**
   * Bei einer Suche mit mehreren Treffern den Ausschnitt darauf legen. Bei
   * genau einem nicht: dann hat `springe` schon hingeflogen, und ein
   * zweites Einpassen danach ruckelt nur.
   *
   * Abhängig vom SUCHTEXT, nicht von der Trefferliste: deren Identität
   * wechselt auch, wenn die Abfrage im Hintergrund neu lädt (beim
   * Zurückwechseln auf den Tab etwa). Dann risse es dem Benutzer den
   * Ausschnitt zurück, den er gerade selbst verschoben hat.
   */
  const trefferRef = useRef(treffer);
  trefferRef.current = treffer;
  useEffect(() => {
    const t = trefferRef.current;
    if (!t || t.length < 2) return;
    karte.current?.fitBounds(L.latLngBounds(t.map((p) => [p.breitengrad, p.laengengrad])), {
      padding: [40, 40],
      maxZoom: 13,
    });
  }, [text]);

  // Ist ein Ort gewählt, dorthin schwenken — sonst sucht man den Punkt,
  // auf den man gerade geklickt hat, in der Seitenspalte wieder.
  useEffect(() => {
    const o = detail.data?.ort;
    if (!o || o.breitengrad === null || o.laengengrad === null) return;
    karte.current?.panTo([o.breitengrad, o.laengengrad]);
  }, [detail.data]);

  const zahlen = useMemo(() => {
    if (!data) return null;
    const mitInhalt = data.punkte.filter((p) => p.personen + p.ereignisse > 0).length;
    return { punkte: data.punkte.length, mitInhalt, ohne: data.ohneKoordinaten };
  }, [data]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Karte</h1>
        {zahlen && (
          <span className="text-sm text-black/50 dark:text-white/50">
            {zahlen.punkte} Orte mit Koordinaten, davon {zahlen.mitInhalt} mit Einträgen
            {zahlen.ohne > 0 && ` · ${zahlen.ohne} noch ohne Koordinaten`}
          </span>
        )}
      </div>

      {zahlen && zahlen.ohne > 0 && (
        // Ohne diesen Hinweis sieht die Karte vollständig aus, obwohl ein
        // Teil fehlt — und niemand käme darauf, die Koordinaten zu holen.
        <p className="mb-4 rounded-lg border border-black/10 p-3 text-sm text-black/60 dark:border-white/10 dark:text-white/60">
          {zahlen.ohne} Orte mit Einträgen haben noch keine Koordinaten und fehlen deshalb
          hier. <code>npm run orte:geokodieren</code> holt sie von OpenStreetMap; im
          Ortsregister lassen sie sich auch von Hand eintragen.
        </p>
      )}

      <div className="mb-3">
        <input
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          onKeyDown={(e) => {
            // Enter nimmt den ersten Treffer — bei einem eindeutigen Namen
            // ist das der ganze Vorgang, ohne Griff zur Maus.
            if (e.key !== "Enter" || !treffer?.[0]) return;
            e.preventDefault();
            springe(treffer[0]);
          }}
          placeholder="Ort suchen … (Name oder Pfad)"
          className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 outline-none
                     focus:border-black/40 dark:border-white/15 dark:bg-neutral-800"
        />

        {treffer && (
          <div className="mt-1 text-sm">
            {treffer.length === 0 ? (
              <p className="px-1 py-1.5 text-black/50 dark:text-white/50">
                Kein Ort mit Koordinaten passt dazu. Orte ohne Koordinaten stehen nicht
                auf der Karte — im Ortsregister sind sie trotzdem zu finden.
              </p>
            ) : (
              <>
                <ul className="divide-y divide-black/5 rounded-lg border border-black/10 dark:divide-white/10 dark:border-white/10">
                  {treffer.slice(0, 8).map((p) => (
                    <li key={p.id}>
                      <button
                        onClick={() => springe(p)}
                        className={`flex w-full items-baseline gap-2 px-3 py-1.5 text-left
                                    hover:bg-black/[0.03] dark:hover:bg-white/5
                                    ${p.id === gewaehlt ? "bg-black/[0.05] dark:bg-white/10" : ""}`}
                      >
                        <span className="flex-1">
                          <span className="font-medium">{p.name}</span>
                          <span className="ml-2 text-xs text-black/40 dark:text-white/40">
                            {p.pfad.split(", ").slice(1).join(", ")}
                          </span>
                        </span>
                        <span className="shrink-0 tabular-nums text-xs text-black/40 dark:text-white/40">
                          {p.personen + p.ereignisse}×
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                {treffer.length > 8 && (
                  <p className="px-1 pt-1 text-xs text-black/40 dark:text-white/40">
                    … und {treffer.length - 8} weitere, auf der Karte hervorgehoben.
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {isPending && <p className="text-black/40">lädt …</p>}

      <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
        <div
          ref={behaelter}
          className="h-[70vh] min-h-80 w-full rounded-lg border border-black/10 dark:border-white/10"
        />

        <div>
          {gewaehlt === null && (
            <p className="text-sm text-black/50 dark:text-white/50">
              Ein Punkt zeigt, wer dort geboren wurde oder gewohnt hat und was dort
              passiert ist. Je größer, desto mehr. Gezählt wird nur, was du sehen darfst —
              Lebende bleiben außen vor, ihr Wohnort ist keine Kartenauskunft.
            </p>
          )}

          {detail.isPending && gewaehlt !== null && (
            <p className="text-sm text-black/40">lädt …</p>
          )}

          {detail.data && (
            <div>
              <div className="mb-3">
                <h2 className="text-lg font-semibold">{detail.data.ort.name}</h2>
                <p className="text-sm text-black/50 dark:text-white/50">
                  {detail.data.ort.pfad.split(", ").slice(1).join(", ") ||
                    ORTSART_LABEL[detail.data.ort.art]}
                  {detail.data.ort.plz && ` · ${detail.data.ort.plz}`}
                </p>
              </div>

              {detail.data.personen.length > 0 && (
                <>
                  <h3 className="mb-1 text-sm font-medium">Personen</h3>
                  <ul className="mb-4 divide-y divide-black/5 text-sm dark:divide-white/10">
                    {detail.data.personen.map((p) => (
                      <li key={p.id}>
                        <Link
                          to={`/person/${p.id}`}
                          className="flex items-baseline gap-2 py-1.5 hover:bg-black/[0.03] dark:hover:bg-white/5"
                        >
                          <span className="flex-1">
                            {p.famname} {p.vorname}
                            <span className="ml-1 text-xs text-black/40 dark:text-white/40">
                              {p.geburtsort && p.wohnort
                                ? "geboren, gewohnt"
                                : p.geburtsort
                                  ? "geboren"
                                  : "gewohnt"}
                            </span>
                          </span>
                          <span className="whitespace-nowrap tabular-nums text-xs text-black/50 dark:text-white/50">
                            {datumKurz(datumAusSpalten(p, "geburt"))}
                            {jahrKurz(datumAusSpalten(p, "tod"))
                              ? `–${jahrKurz(datumAusSpalten(p, "tod"))}`
                              : ""}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {detail.data.ereignisse.length > 0 && (
                <>
                  <h3 className="mb-1 text-sm font-medium">Ereignisse</h3>
                  <ul className="divide-y divide-black/5 text-sm dark:divide-white/10">
                    {detail.data.ereignisse.map((e) => (
                      <li key={e.id} className="py-1.5">
                        <span className="font-medium">
                          {EREIGNIS_LABEL[e.art as keyof typeof EREIGNIS_LABEL] ?? e.art}
                        </span>
                        {wann(e) && (
                          <span className="ml-2 text-black/50 dark:text-white/50">{wann(e)}</span>
                        )}
                        {e.titel && (
                          <span className="ml-2 text-black/50 dark:text-white/50">{e.titel}</span>
                        )}
                        {e.beteiligte.length > 0 && (
                          <span className="ml-1 block text-xs">
                            {e.beteiligte.map((t, i) => (
                              <span key={t.id}>
                                {i > 0 && ", "}
                                <Link to={`/person/${t.id}`} className="hover:underline">
                                  {t.vorname} {t.famname}
                                </Link>
                              </span>
                            ))}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {detail.data.personen.length === 0 && detail.data.ereignisse.length === 0 && (
                <p className="text-sm text-black/50 dark:text-white/50">
                  Hier ist nichts eingetragen, das du sehen darfst.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
